import { UseFormReturn } from 'react-hook-form';

import { ValidationStatus } from '@/components/datamodel/hooks/useValidationsStore';
import { BaseValidation } from '@/components/datamodel/types';
import { logger } from '@/services/logger';

interface ValidationResponse {
    attributeStates?: {
        [key: string]: ('hidden' | 'readonly')[];
    };
    calculations: {
        [key: string]: string | number;
    };
    validations: {
        [key: string]: BaseValidation[];
    };
}

/**
 * Handles validation response success logic including:
 * - Tracking validated values
 * - Applying calculated values
 * - Managing validation tracking state
 */
export const handleValidationSuccess = async (
    validationResponse: ValidationResponse,
    variables: { attributeNames: string[] | null; attributeStatesOnly?: boolean },
    updateItemWithCalculatedValues: null | ((attributeName: string, value: any) => void),
    getValues: UseFormReturn<any>['getValues'],
    calculatedAttributesRef: React.MutableRefObject<Set<string>>,
    applyAttributeStates: (attributeStates: Record<string, string[]>) => void,
    setValidationsOnAttributes: (
        validations: Record<string, any[]>,
        validatedAttributeNames?: string[],
        initialStatus?: any
    ) => Promise<void>
) => {
    // We'd like to update the validation results only
    // when attributeStatesOnly wasn't set
    if (!variables.attributeStatesOnly) {
        const attributeNames = variables.attributeNames || undefined;
        await setValidationsOnAttributes(validationResponse.validations, attributeNames);
    }

    if (validationResponse.attributeStates) {
        applyAttributeStates(validationResponse.attributeStates);
    }

    // Apply calculated values
    if (validationResponse.calculations) {
        // Track calculated attributes to prevent validation triggers
        const calculatedAttributeNames = Object.keys(validationResponse.calculations);
        calculatedAttributesRef.current = new Set(calculatedAttributeNames);

        Object.entries(validationResponse.calculations).forEach(([attributeName, calculatedAttributeValue]) => {
            const currentValue = getValues(attributeName);
            if (String(calculatedAttributeValue) !== String(currentValue)) {
                if (updateItemWithCalculatedValues) {
                    updateItemWithCalculatedValues(attributeName, calculatedAttributeValue);
                }
            }
        });
    } else {
        // Clear calculated attributes if no calculations in response
        calculatedAttributesRef.current.clear();
    }
};

/**
 * Handles validation response error logic including:
 * - Clearing tracking sets on error
 * - Error handling and status updates
 */
export const handleValidationError = (
    error: any,
    variables: { attributeNames: string[] | null; attributeStatesOnly?: boolean } | undefined,
    setValidationStatus: (status: any) => void,
    toast: { error: (message: string) => void },
    t: (key: string) => string
) => {
    // Check for different possible error code properties
    const errorCode = error?.response?.data?.errorCode ||
                        (error?.response?.data as any)?.code ||
                        (error?.response?.data as any)?.status ||
                        (error?.response as any)?.status;

    if (errorCode !== undefined) {
        toast.error(t('ERROR_TECHNICAL'));

        if (!variables?.attributeStatesOnly) {
            setValidationStatus(ValidationStatus.IS_FAILED);
        }
    } else {
        logger.error('Got error from validation: ' + error);
    }
};

